import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Paper, Typography, Box, Snackbar, Alert } from "@mui/material";
import { UserForm, UserFormData } from "./components/UserForm";
import { useCreateUser } from "../../../../api/bpm/endpoints";
import { useAuth } from "../../../../auth";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/__auth/admin/users/create")({
  component: CreateUserPage,
  staticData: {
    title: "admin.users.createTitle",
  },
});

function CreateUserPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const tenant = user?.tenant || "default-domain";

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const createMutation = useCreateUser({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["listUser"],
        });
        setSnackbar({
          open: true,
          message: t("admin.users.createSuccess"),
          severity: "success",
        });
        setTimeout(
          () => navigate({ to: "/admin/users", search: { page: 1, size: 10 } }),
          1500,
        );
      },
      onError: (error: any) => {
        const apiMessage =
          error?.response?.data?.message ||
          error?.message ||
          t("admin.users.createError");
        setSnackbar({
          open: true,
          message: apiMessage,
          severity: "error",
        });
      },
    },
  });

  const handleSubmit = async (data: UserFormData) => {
    const payload = {
      tenant,
      data: {
        name: data.name,
        id: data.email,
        email: data.email,
        cpf: (data.cpf || null) as unknown as string,
        matriculaInstitucional: data.matriculaInstitucional || undefined,
        dataNascimento: data.dataNascimento || undefined,
        stockingAreas: data.stockingAreas?.map((sa: any) => ({ id: sa.id })),
        job: data.job ? { id: data.job.id } : undefined,
        occupation: data.occupation ? { id: data.occupation.id } : undefined,
        accessProfiles: data.accessProfiles?.map((ap: any) => ({
          id: ap.id,
          isUnderteminedTime: true,
        })),
        groups: data.groups?.map((g: any) => ({
          id: g.id,
          isUnderteminedTime: true,
        })),
      },
    };

    createMutation.mutate(payload);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        {t("admin.users.createTitle")}
      </Typography>
      <Paper sx={{ p: 3, mt: 3 }}>
        <UserForm
          onSubmit={handleSubmit}
          onCancel={() =>
            navigate({ to: "/admin/users", search: { page: 1, size: 10 } })
          }
          isLoading={createMutation.isPending}
        />
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
